/**
 * Debounced quantity input for the cart and quick cart.
 *
 * The theme applies quantity changes on the native `change` event, which only
 * fires on blur. This applies them once the customer has stopped typing, then
 * puts the caret back: the cart section re-renders by replacing innerHTML, so
 * the input the customer was typing into is destroyed and recreated.
 *
 * Work is handed back to the theme by dispatching a bubbling `change`, which
 * its delegated listener already handles (cart.updateItem + section refresh).
 */
(function () {
  var DEBOUNCE_MS = 1000;
  var ROOTS = '[data-section-type="cart"], [data-section-type="quick-cart"]';
  var INPUT = '.quantity-input__input';
  var ITEM = '[data-input-item]';

  // If an update fails there is no re-render, so give up the focus claim rather
  // than hold it and pounce on some later, unrelated mutation.
  var RESTORE_TIMEOUT_MS = 3000;

  var timer = null;
  var syntheticTarget = null;

  // Quantity the cart currently holds per line item, as far as we know.
  var applied = Object.create(null);

  // The field the customer is typing in, tracked across re-renders by line key.
  var active = null;

  // True between tearing the field out and putting focus back. Chrome fires
  // focusout during the re-render while the input is still connected and still
  // matches the section root, so it is indistinguishable from a real blur by
  // inspection -- we have to remember that we caused it.
  var pendingRestore = false;
  var restoreDeadline = null;

  // Set while we focus the field ourselves, so the focusin handler doesn't bank
  // text the customer hasn't submitted yet as the cart's current quantity.
  var restoring = false;

  function releaseFocusClaim() {
    pendingRestore = false;
    clearTimeout(restoreDeadline);
    restoreDeadline = null;
  }

  // A detached node can't reach the section root, so this also filters out
  // inputs that the cart re-render has already thrown away.
  function cartInput(el) {
    return el && el.matches && el.matches(INPUT) && el.closest(ROOTS) ? el : null;
  }

  function keyFor(input) {
    var item = input.closest(ITEM);
    return item ? item.dataset.key : null;
  }

  // Always resolve the live node. The section re-render swaps inputs out, and the
  // theme listens for `change` in the bubble phase on the section root, so an
  // event dispatched at a detached node would never reach it.
  function inputForKey(key) {
    if (!key) return null;
    return document.querySelector(ITEM + '[data-key="' + key + '"] ' + INPUT);
  }

  function quantityOf(input) {
    var qty = parseInt(input.value, 10);
    return isFinite(qty) ? qty : null;
  }

  function apply(key) {
    var input = inputForKey(key);
    if (!input) return;

    var qty = quantityOf(input);

    // Never auto-submit an empty or zero quantity: 0 removes the line item, and
    // the field is transiently empty while the customer retypes it.
    if (qty === null || qty < 1) return;
    if (applied[key] === qty) return;

    applied[key] = qty;

    // Only claim focus restoration if we are about to tear out the field the
    // customer is actually in.
    if (document.activeElement === input) {
      pendingRestore = true;
      clearTimeout(restoreDeadline);
      restoreDeadline = setTimeout(releaseFocusClaim, RESTORE_TIMEOUT_MS);
    }

    syntheticTarget = input;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    syntheticTarget = null;
  }

  document.addEventListener(
    'focusin',
    function (e) {
      if (restoring) return;
      var input = cartInput(e.target);
      if (!input) return;
      var key = keyFor(input);
      var qty = quantityOf(input);
      if (key && qty !== null) applied[key] = qty;
    },
    true
  );

  document.addEventListener(
    'input',
    function (e) {
      var input = cartInput(e.target);
      if (!input) return;

      var key = keyFor(input);
      if (!key) return;

      active = { key: key, value: input.value };

      clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        apply(key);
      }, DEBOUNCE_MS);
    },
    true
  );

  // The theme still fires `change` on blur. Swallow it when it would just repeat
  // the quantity we already sent; otherwise let it through and drop our timer so
  // the same edit isn't submitted twice.
  document.addEventListener(
    'change',
    function (e) {
      var input = cartInput(e.target);
      if (!input || input === syntheticTarget) return;

      var key = keyFor(input);
      var qty = quantityOf(input);
      if (!key) return;

      if (qty !== null && applied[key] === qty) {
        e.stopPropagation();
        return;
      }

      clearTimeout(timer);
      timer = null;
      if (qty !== null && qty >= 1) applied[key] = qty;

      // Tearing out a field that holds uncommitted text makes the browser fire a
      // native `change` on the way out. The theme acts on it and re-renders
      // again, so claim that second teardown too or the caret is lost. A real
      // blur also lands here, but focusout releases the claim once it sees a
      // relatedTarget, and pointerdown clears `active` on a click away.
      if (active && active.key === key) {
        active.value = input.value;
        pendingRestore = true;
        clearTimeout(restoreDeadline);
        restoreDeadline = setTimeout(releaseFocusClaim, RESTORE_TIMEOUT_MS);
      }
    },
    true
  );

  // A click anywhere but the field the customer is editing is a deliberate move
  // away. Fires before focusout, so it settles ownership first.
  document.addEventListener(
    'pointerdown',
    function (e) {
      if (!active) return;
      var input = inputForKey(active.key);
      if (input && (e.target === input || input.contains(e.target))) return;
      active = null;
      releaseFocusClaim();
    },
    true
  );

  document.addEventListener(
    'focusout',
    function (e) {
      if (!cartInput(e.target)) return;

      // Focus landed on a real element, so the customer moved it (Tab, click).
      // The re-render teardown always reports a null relatedTarget.
      if (e.relatedTarget) {
        active = null;
        releaseFocusClaim();
        return;
      }

      // Either an edit is still settling or we are mid-teardown. Keep tracking
      // so restore() can put the caret back.
      if (timer || pendingRestore) return;

      active = null;
    },
    true
  );

  // Focus with the caret after the last digit, so the customer can keep typing.
  // On input[type=number] setSelectionRange throws InvalidStateError, and plain
  // focus() leaves the caret at position 0 -- typing 5 into "110" would give
  // "5110". Swapping to type=text for the call is the only approach that works,
  // and focus survives swapping back.
  function focusAtEnd(input) {
    restoring = true;

    var isNumber = input.type === 'number';
    if (isNumber) input.type = 'text';

    input.focus({ preventScroll: true });
    try {
      var end = input.value.length;
      input.setSelectionRange(end, end);
    } catch (err) {
      // Non-text input types that still reject selection: leave the caret alone.
    }

    if (isNumber) input.type = 'number';

    restoring = false;
  }

  function restore() {
    if (!active || !pendingRestore) return;

    var input = inputForKey(active.key);

    // The line item is gone (removed, or the cart emptied). Nothing to return to.
    if (!input) {
      if (!timer) {
        active = null;
        releaseFocusClaim();
      }
      return;
    }

    // Still focused, so nothing was torn out. Rewriting the value here would drag
    // the caret to the end mid-word on any unrelated mutation. Hold the claim:
    // one edit can trigger a second re-render, and that teardown is ours too.
    if (document.activeElement === input) return;

    // The re-render drops focus to the body. Anything else means the customer
    // took focus somewhere in the meantime, and it is not ours to take back.
    var current = document.activeElement;
    if (current && current !== document.body && current !== document.documentElement) {
      active = null;
      releaseFocusClaim();
      return;
    }

    // Trust the re-rendered value: Shopify clamps the quantity to available stock.
    // Read it before focusing, while the field still holds what the server sent.
    var rendered = quantityOf(input);
    if (rendered !== null) applied[active.key] = rendered;

    // A pending timer means more digits were typed while the update was in
    // flight, so the customer's text wins over the value we just rendered.
    if (timer) input.value = active.value;

    focusAtEnd(input);

    // The claim is deliberately not released here. Applying one edit can produce
    // two re-renders, and only a real focus move or the deadline should end it.
  }

  function init() {
    var roots = document.querySelectorAll(ROOTS);
    if (!roots.length) return;

    var observer = new MutationObserver(restore);
    Array.prototype.forEach.call(roots, function (root) {
      observer.observe(root, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
